import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAddContentProgressMutation } from 'src/redux/slices/api/su';
import { useParentEntityContext } from 'src/contexts/ParentEntityContext';
import { useClientId } from 'src/contexts/ClientContext';
import { InstanceStateData } from 'src/types/state/learningModule';
import { extractRefTag, extractReferral } from 'src/utils/refParameterConversion';

/**
 * Records a PDF instance as viewed-to-completion when it is shown. PDF views are treated as
 * immediately completed, so the call reports `lastWatchedPosition === contentLength === pageSize`;
 * the backend expects this call even when `pageSize` is 0. Parent entity and ref/referral
 * attribution come from the surrounding `ParentEntityProvider` and the page URL.
 *
 * @param instance - The PDF instance being displayed
 * @param clientIdOverride - Client id to attribute the view to; falls back to the client context
 */
const useDocumentCompletionProgress = (
  instance: InstanceStateData,
  clientIdOverride?: string,
): void => {
  const [addContentProgress] = useAddContentProgressMutation();
  const { parentId, parentType } = useParentEntityContext();
  const contextClientId = useClientId();
  const [searchParams] = useSearchParams();
  const clientId = clientIdOverride ?? contextClientId;

  useEffect(() => {
    const pdfPageSize = instance.pageSize || 0;
    addContentProgress({
      instance,
      createdAt: Date.now().valueOf(),
      progress: {
        lastWatchedPosition: pdfPageSize,
        contentLength: pdfPageSize,
      },
      clientId,
      refTag: extractRefTag(searchParams),
      referral: extractReferral(searchParams),
      parentId,
      parentType,
    });
  }, [instance, addContentProgress, clientId, searchParams, parentId, parentType]);
};

export default useDocumentCompletionProgress;
